import { FC, useMemo, useState } from "react";
import { types, ModalProps, toast } from "@vilocnv/allsetra-core";
import { Form, Formik, FormikHelpers } from "formik";
import InnerForm from "./children/InnerForm";
import UpdateObjectRideConfirmationModal from "./children/UpdateObjectRideConfirmationModal";

// Data
import { isEmpty } from "lodash";
import {
  transformObjectRideDataForForm,
  updateObjectRideFormValidationSchema,
  OBJECT_RIDE_MODES_OPTIONS
} from "app/data/helpers";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  getObjectSpecificRideInfoThunk,
  setObjectSpecificRide,
  updateObjectRideThunk
} from "app/features";
import {
  selectDrawerSelectedAccountId,
  selectAccountKeysState,
  selectAccountsState,
  selectObjectSpecificRide
} from "app/data/selectors";
import { SignalRService } from "app/data/services";
import { useTranslation } from "react-i18next";

type Props = Omit<ModalProps, "title" | "children"> & {
  objectRide: types.IObjectRide | null;
  objectId: string;
};

type RideFormValues = Omit<types.IUpdateObjectRide, "privateMileage"> & {
  startAddress: string;
  endAddress: string;
  changeReason?: string;
  privateMileage?: number | string | null;
};

const buildOptimisticRide = (
  ride: types.IObjectRide,
  values: RideFormValues,
  accountKeys: any[],
  usersBasicInfo: any[]
): types.IObjectRide => {
  const rideMode = Number(values.rideMode);
  const optimisticRide: types.IObjectRide = {
    ...ride,
    rideMode,
    comments: values.comments || null,
    changeReason: values.changeReason,
    correctedStartAddress:
      values.startAddress ||
      values.correctedStartAddress ||
      ride.correctedStartAddress,
    correctedEndAddress:
      values.endAddress ||
      values.correctedEndAddress ||
      ride.correctedEndAddress
  } as types.IObjectRide;

  const privateMileage = values.privateMileage;
  if (
    privateMileage !== undefined &&
    privateMileage !== null &&
    `${privateMileage}` !== ""
  ) {
    (optimisticRide as any).privateMileage = Number(privateMileage);
  } else if (rideMode === 1 || rideMode === 3) {
    (optimisticRide as any).privateMileage = 0;
  }

  const originalKeyId = (ride as any).key?.uniqueId || "";
  const originalDriverId =
    (ride as any).driver?.uniqueId || (ride as any).driverId || "";
  const keyChanged = Boolean(values.keyId) && values.keyId !== originalKeyId;
  const driverChanged =
    Boolean(values.driverId) && values.driverId !== originalDriverId;

  // Only rewrite key/driver when the user actually changed them. Comment-only
  // edits keep keyId in the form and must not wipe an existing driver.
  if (keyChanged) {
    const key = accountKeys?.find(
      (item: any) => item.uniqueId === values.keyId
    );
    (optimisticRide as any).key = key
      ? { ...(ride as any).key, ...key, uniqueId: values.keyId }
      : { uniqueId: values.keyId };
    (optimisticRide as any).driver = null;
    (optimisticRide as any).driverId = null;
  } else if (driverChanged) {
    const driver = usersBasicInfo?.find(
      (user: any) => user.uniqueId === values.driverId
    );
    (optimisticRide as any).driver = driver
      ? { ...(ride as any).driver, ...driver, uniqueId: values.driverId }
      : { uniqueId: values.driverId };
    (optimisticRide as any).driverId = values.driverId;
    (optimisticRide as any).key = null;
  }

  return optimisticRide;
};

const mergeOptimisticFields = (
  serverRide: types.IObjectRide,
  optimisticRide: types.IObjectRide,
  values: RideFormValues
): types.IObjectRide =>
  ({
    ...serverRide,
    rideMode: Number(values.rideMode),
    comments: optimisticRide.comments,
    changeReason: (optimisticRide as any).changeReason,
    privateMileage: (optimisticRide as any).privateMileage,
    correctedStartAddress: optimisticRide.correctedStartAddress,
    correctedEndAddress: optimisticRide.correctedEndAddress,
    key: (optimisticRide as any).key,
    driver: (optimisticRide as any).driver,
    driverId: (optimisticRide as any).driverId
  }) as types.IObjectRide;

const UpdateObjectRideForm: FC<Props> = ({
  open,
  onClose,
  objectRide,
  objectId
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation"]);

  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { accountKeys } = useAppSelector(selectAccountKeysState);
  const { usersBasicInfo } = useAppSelector(selectAccountsState);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState<any>(null);

  const initialValues = useMemo(
    () =>
      !isEmpty(objectRide) ? transformObjectRideDataForForm(objectRide) : {},
    [objectRide]
  );

  const onFulfilledRequest = async (
    params: { accountId: string; objectId: string; rideId: string },
    formikHelpers: FormikHelpers<types.IUpdateObjectRide>,
    values: RideFormValues
  ) => {
    formikHelpers.setSubmitting(false);
    setIsSubmitting(false);

    toast.success(t("toasts.objectRideUpdatedSuccess"));

    // Optimistic drawer update: detail GET can fail or lag for Admin/private rides.
    if (objectRide) {
      const optimisticRide = buildOptimisticRide(
        objectRide,
        values,
        accountKeys ?? [],
        usersBasicInfo ?? []
      );

      const applyIfStillCurrentRide = (ride: types.IObjectRide) => {
        dispatch((innerDispatch, getState) => {
          const currentRide = selectObjectSpecificRide(getState());
          if (currentRide?.uniqueId && currentRide.uniqueId !== params.rideId) {
            return;
          }
          innerDispatch(setObjectSpecificRide(ride));
        });
      };

      applyIfStillCurrentRide(optimisticRide);

      const result = await dispatch(getObjectSpecificRideInfoThunk(params));

      if (result.type === "objects/getObjectSpecificRideInfoThunk/rejected") {
        applyIfStillCurrentRide(optimisticRide);
      } else if (
        result.type === "objects/getObjectSpecificRideInfoThunk/fulfilled"
      ) {
        const serverRide = result.payload as types.IObjectRide | undefined;
        if (serverRide) {
          // Always overlay submitted fields: a same-mode lagging GET would
          // otherwise restore stale comments/driver/privateMileage.
          applyIfStillCurrentRide(
            mergeOptimisticFields(serverRide, optimisticRide, values)
          );
        }
      }
    } else {
      dispatch(getObjectSpecificRideInfoThunk(params));
    }

    setIsConfirmationModalOpen(false);
    setFormDataToSubmit(null);
    onClose();
  };

  const saveChangesHandler = async (
    values: RideFormValues,
    formikHelpers: FormikHelpers<types.IUpdateObjectRide>
  ) => {
    setFormDataToSubmit({ values, formikHelpers });
    setIsConfirmationModalOpen(true);
    formikHelpers.setSubmitting(false);
  };

  const handleConfirmationSubmit = async () => {
    if (!formDataToSubmit) return;

    const { values, formikHelpers } = formDataToSubmit;

    if (!objectId || !objectRide?.uniqueId) {
      toast.error(t("errors.objects.rideNotFound"));
      return;
    }

    try {
      formikHelpers.setSubmitting(true);
      setIsSubmitting(true);
      setIsConfirmationModalOpen(false);

      const params: { accountId: string; objectId: string; rideId: string } = {
        accountId: accountId ?? "",
        objectId,
        rideId: objectRide.uniqueId
      };

      const rideMode = Number(values.rideMode);
      const isBusinessOrCommute = rideMode === 1 || rideMode === 3;

      const data: any = {
        changeReason: values.changeReason,
        uniqueId: values.uniqueId,
        rideMode: rideMode,
        comments: values.comments || null,
        startAddressPointOfInterest: values.startAddressPointOfInterest || null,
        endAddressPointOfInterest: values.endAddressPointOfInterest || null,
        correctedEndAddress: values.correctedEndAddress || null,
        correctedEndLatitude: values.correctedEndLatitude || null,
        correctedEndLongitude: values.correctedEndLongitude || null,
        correctedStartAddress: values.correctedStartAddress || null,
        correctedStartLatitude: values.correctedStartLatitude || null,
        correctedStartLongitude: values.correctedStartLongitude || null
      };

      if (values.privateMileage) {
        data.privateMileage = Number(values.privateMileage);
      } else if (isBusinessOrCommute) {
        data.privateMileage = 0;
      }

      if (values.startAddress) {
        data.correctedStartAddress = values.startAddress;
      }
      if (values.endAddress) {
        data.correctedEndAddress = values.endAddress;
      }

      if (values.keyId) {
        data.keyId = values.keyId;
      } else if (values.driverId) {
        data.driverId = values.driverId;
      }

      // Clean up: remove null/undefined/empty values
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (
          value === undefined ||
          value === "" ||
          (value === null && key !== "comments")
        ) {
          delete data[key];
        }
      });

      const { type } = await dispatch(
        updateObjectRideThunk({ ...params, data })
      );

      if (type === "objects/updateObjectRideThunk/fulfilled") {
        let hasCompleted = false;

        const completeUpdate = () => {
          if (hasCompleted) return;
          hasCompleted = true;
          onFulfilledRequest(params, formikHelpers, values);
        };

        // Wait for backend propagation via SignalR (or 10s fallback), matching bulk ride updates.
        const timeoutId = setTimeout(() => {
          SignalRService.hubConnection?.off("EventRaised");
          completeUpdate();
          console.log("Stopped listening for EventRaised after 10 seconds.");
        }, 10000);

        const handleEventRaised = (event: any) => {
          if (event.eventName === types.BackendEventsEnum.RideUpdatedEvent) {
            clearTimeout(timeoutId);
            SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
            completeUpdate();
          }
        };

        SignalRService.hubConnection?.on("EventRaised", handleEventRaised);
      } else {
        formikHelpers.setSubmitting(false);
        setIsSubmitting(false);
      }
    } catch (e: any) {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
    setFormDataToSubmit(null);
  };

  // Get labels for confirmation modal
  const getDriverEmail = (driverId: string | undefined) => {
    if (!driverId) return undefined;
    const driver = usersBasicInfo?.find(
      (user: any) => user.uniqueId === driverId
    );
    return driver?.email;
  };

  const getKeyName = (keyId: string | undefined) => {
    if (!keyId) return undefined;
    const key = accountKeys?.find((key: any) => key.uniqueId === keyId) as any;
    return key?.keyName;
  };

  const getRideModeLabel = (rideMode: number | undefined) => {
    if (rideMode === undefined) return undefined;
    const modes = OBJECT_RIDE_MODES_OPTIONS(t);
    const mode = modes.find((m: any) => m.value === rideMode);
    return mode?.label;
  };

  return (
    <>
      <Formik
        // @ts-ignore
        initialValues={initialValues}
        validationSchema={updateObjectRideFormValidationSchema(t)}
        // @ts-ignore
        onSubmit={saveChangesHandler}
        enableReinitialize
        validateOnMount
      >
        <Form>
          <InnerForm
            open={open}
            onClose={onClose}
            customIsSubmitting={isSubmitting}
            objectRide={objectRide}
          />
        </Form>
      </Formik>

      <UpdateObjectRideConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationCancel}
        onConfirm={handleConfirmationSubmit}
        formData={formDataToSubmit?.values || null}
        driverEmail={getDriverEmail(formDataToSubmit?.values?.driverId)}
        keyName={getKeyName(formDataToSubmit?.values?.keyId)}
        rideModeLabel={getRideModeLabel(formDataToSubmit?.values?.rideMode)}
      />
    </>
  );
};

export default UpdateObjectRideForm;
